// src/components/today/QuickCaptureSection.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Capture } from '../../types/capture';
import { PenLine, Plus, Maximize2 } from '../common/icons';
import { format } from 'date-fns';

interface QuickCaptureSectionProps {
  recentCaptures: Capture[];
  captureInput: string;
  onCaptureInputChange: (value: string) => void;
  onSubmitCapture: () => void;
  isSubmitting: boolean;
  onExpand?: () => void; // 新增：點擊展開圖標時的回調
  isFullWidth?: boolean; // 全寬模式：左右分欄佈局
}

const QuickCaptureSection: React.FC<QuickCaptureSectionProps> = ({
  recentCaptures,
  captureInput,
  onCaptureInputChange,
  onSubmitCapture,
  isSubmitting,
  onExpand,
  isFullWidth = false,
}) => {
  // 檢測作業系統和裝置
  const [isMac, setIsMac] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMac(navigator.platform.toUpperCase().indexOf('MAC') >= 0);
    setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
  }, []);

  // 處理快捷鍵
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      onSubmitCapture();
    }
  }, [onSubmitCapture]);

  // 文字截斷功能
  const truncateText = useCallback((text: string, maxLength: number = 35): string => {
    if (text.length <= maxLength) return text;

    // 嘗試在單詞邊界截斷
    const truncated = text.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');

    // 如果最後一個空格位置太靠前，直接截斷
    if (lastSpaceIndex > maxLength * 0.7) {
      return truncated.substring(0, lastSpaceIndex) + '...';
    }

    return truncated + '...';
  }, []);

  // 格式化時間
  const formatCaptureTime = useCallback((date: Date): string => {
    return format(date, 'HH:mm');
  }, []);

  // 輸入區（共用）
  const inputArea = (
    <div className={isFullWidth ? '' : 'mb-4'}>
      <div className="relative">
        <textarea
          value={captureInput}
          onChange={(e) => onCaptureInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="What's on your mind?"
          rows={isFullWidth ? 4 : 3}
          className="w-full p-3 pr-10 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200"
          disabled={isSubmitting}
        />
        {/* 展開圖標 */}
        {onExpand && (
          <button
            type="button"
            onClick={onExpand}
            className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-sky-600 hover:bg-sky-50 rounded-md transition-all duration-200"
            aria-label="展開編輯"
            disabled={isSubmitting}
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* 操作區域 */}
      <div className="flex items-center justify-between mt-3">
        {/* 快捷鍵提示 - 只在桌面版顯示 */}
        {!isMobile && (
          <span className="text-xs text-gray-500">
            Press {isMac ? 'Cmd' : 'Ctrl'}+Enter to capture
          </span>
        )}
        {isMobile && <div></div>}

        {/* 提交按鈕 */}
        <button
          onClick={onSubmitCapture}
          disabled={!captureInput.trim() || isSubmitting}
          className="btn-accent btn-sm"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Capturing...</span>
            </>
          ) : (
            <>
              <Plus size={14} />
              <span>Capture</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Recent Captures 區（共用）
  const recentArea = recentCaptures.length > 0 ? (
    <div className={isFullWidth ? '' : 'pt-4 border-t border-gray-100'}>
      <h4 className="text-xs font-medium text-gray-600 mb-3">
        Recent captures
      </h4>
      <div className="space-y-2">
        {recentCaptures.slice(0, 4).map((capture) => (
          <div
            key={capture.id}
            className="flex gap-3 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-150"
          >
            <span className="text-xs text-gray-500 font-mono mt-0.5 flex-shrink-0">
              {formatCaptureTime(capture.createdAt)}
            </span>
            <span className="text-xs text-gray-700 leading-relaxed">
              {truncateText(capture.content)}
            </span>
          </div>
        ))}
      </div>
    </div>
  ) : (
    <div className={isFullWidth ? '' : 'pt-4 border-t border-gray-100'}>
      <div className={isFullWidth ? '' : 'text-center py-4'}>
        <PenLine size={20} className={`text-gray-400 mb-2 ${isFullWidth ? '' : 'mx-auto'}`} />
        <span className="text-xs text-gray-500">
          No recent captures
        </span>
      </div>
    </div>
  );

  if (isFullWidth) {
    // 全寬版：輸入框全寬 + 下方 2 欄 grid Recent captures
    const fullWidthInputArea = (
      <div className="mb-4">
        <div className="relative">
          <textarea
            value={captureInput}
            onChange={(e) => onCaptureInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="What's on your mind?"
            rows={4}
            className="w-full p-3 pr-10 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200"
            disabled={isSubmitting}
          />
          {onExpand && (
            <button
              type="button"
              onClick={onExpand}
              className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-sky-600 hover:bg-sky-50 rounded-md transition-all duration-200"
              aria-label="展開編輯"
              disabled={isSubmitting}
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between mt-3">
          {!isMobile && (
            <span className="text-xs text-gray-500">
              Press {isMac ? 'Cmd' : 'Ctrl'}+Enter to capture
            </span>
          )}
          {isMobile && <div></div>}
          <button
            onClick={onSubmitCapture}
            disabled={!captureInput.trim() || isSubmitting}
            className="btn-accent btn-sm"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Capturing...</span>
              </>
            ) : (
              <>
                <Plus size={14} />
                <span>Capture</span>
              </>
            )}
          </button>
        </div>
      </div>
    );

    const fullWidthRecentArea = recentCaptures.length > 0 ? (
      <div className="pt-4 border-t border-gray-100">
        <h4 className="text-xs font-medium text-gray-600 mb-3">Recent captures</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {recentCaptures.slice(0, 4).map((capture) => (
            <div
              key={capture.id}
              className="flex gap-3 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-150"
            >
              <span className="text-xs text-gray-500 font-mono mt-0.5 flex-shrink-0">
                {formatCaptureTime(capture.createdAt)}
              </span>
              <span className="text-xs text-gray-700 leading-relaxed truncate">
                {truncateText(capture.content, 40)}
              </span>
            </div>
          ))}
        </div>
      </div>
    ) : (
      <div className="pt-4 border-t border-gray-100 flex items-center gap-2">
        <PenLine size={16} className="text-gray-400 flex-shrink-0" />
        <span className="text-xs text-gray-500">No recent captures</span>
      </div>
    );

    return (
      <div className="p-5 md:p-8 rounded-xl md:rounded-1.5xl bg-white border border-gray-200">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-5">
          QUICK CAPTURE
        </div>
        {fullWidthInputArea}
        {fullWidthRecentArea}
      </div>
    );
  }

  return (
    <div className="p-5 md:p-6 rounded-xl md:rounded-1.5xl bg-white border border-gray-200">
      <div>
        {/* 標題 */}
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
          QUICK CAPTURE
        </div>

        {/* 輸入區域 */}
        {inputArea}

        {/* Recent Captures */}
        {recentArea}
      </div>
    </div>
  );
};

export default QuickCaptureSection;